/**
 * Shared leg/coverage derivations (v2.5 SS-03 / PR C1, G0 §6).
 *
 * The SynthesisBand and the PillarCard previously carried TWIN copies of the
 * hard-floor breach check, and both read leg STATE where coverage
 * SUFFICIENCY was the real question — which is how "sufficient coverage
 * across all pillars" rendered over 27% preliminary coverage. ONE module now
 * owns these derivations; keep it aligned with the server's
 * patternIdentifier gating + confidence derivation.
 *
 * Composition follows the approved G0 §6.1 exactly — basis first, joined by
 * a middle dot, with the coverage percentage stated:
 *   - "Suspected · Perceived coverage preliminary (27%)"
 *   - "Confirmed by the two available legs" (declared absent)
 *   - "Provisional · Technical coverage preliminary (26%)"
 *   - "Confirmed"
 *   - "Legacy interpretation" (pattern recorded before the v2.5 methodology)
 */

import type { TriangulationCell } from '../../types/triangulation';

export type HardFloorLeg = 'technical' | 'perceived';

/** Hard-floor breach: the leg is missing entirely or below its coverage
 *  hard floor — pattern emission is gated off server-side. */
export function legBreached(cell: TriangulationCell, leg: HardFloorLeg): boolean {
  const l = cell[leg];
  return l.state !== 'present_and_scored' || l.coverage.sufficiency === 'insufficient';
}

/** True only when the leg contributed scored signal AT sufficient coverage —
 *  preliminary coverage is NOT sufficiency (the SS-03 conflation). */
export function legSufficient(cell: TriangulationCell, leg: HardFloorLeg | 'documented'): boolean {
  const l = cell[leg];
  return l.state === 'present_and_scored' && l.coverage.sufficiency === 'sufficient';
}

export interface PreliminaryLegDetail {
  leg: 'Technical' | 'Perceived' | 'Documented';
  /** G0 §6.1 coverage phrase: "Perceived coverage preliminary (27%)" for
   *  ratio-banded legs; "Documented coverage preliminary (2 of 6 documents)"
   *  for the count-based Documented leg. */
  phrase: string;
}

/** The CONTRIBUTING legs whose coverage is preliminary — the facts behind
 *  coverage_confidence === 'preliminary', with the G0 coverage phrase. */
export function preliminaryLegDetails(cell: TriangulationCell): PreliminaryLegDetail[] {
  const out: PreliminaryLegDetail[] = [];
  const check = (
    key: 'technical' | 'perceived' | 'documented',
    label: 'Technical' | 'Perceived' | 'Documented'
  ) => {
    const l = cell[key];
    if (l.state !== 'present_and_scored' || l.coverage.sufficiency !== 'preliminary') return;
    // C2: an unreviewed/too-few basis has NO legitimate percentage — the
    // phrase states the basis fact instead of a number.
    const phrase =
      key === 'documented'
        ? `${label} coverage preliminary (${l.coverage.evaluated} of ${l.coverage.total} documents)`
        : l.coverage.basis === 'unreviewed'
          ? `${label} target population unreviewed (${l.coverage.evaluated} of ${l.coverage.total} candidates)`
          : l.coverage.pct === null
            ? `${label} coverage preliminary (${l.coverage.evaluated} of ${l.coverage.total} confirmed targets)`
            : `${label} coverage preliminary (${Math.round(l.coverage.pct * 100)}%)`;
    out.push({ leg: label, phrase });
  };
  check('technical', 'Technical');
  check('perceived', 'Perceived');
  check('documented', 'Documented');
  return out;
}

/** Back-compat helper: just the leg names. */
export function preliminaryLegs(cell: TriangulationCell): Array<'Technical' | 'Perceived' | 'Documented'> {
  return preliminaryLegDetails(cell).map((d) => d.leg);
}

/** The declared-absence basis sentence (G0 §6.1), with attribution when the
 *  domain carries the actor snapshot. */
export function declaredAbsentSentence(
  declaredByName?: string | null,
  declaredAt?: string | null
): string {
  const who = declaredByName ? ` by ${declaredByName}` : '';
  const when = declaredAt ? ` on ${new Date(declaredAt).toLocaleDateString()}` : '';
  return `Documented signal declared deliberately absent during assessment setup${who}${when}.`;
}

/**
 * Compose the user-facing confidence label per G0 §6.1 — basis first,
 * middle-dot joined, coverage percentages stated. Null when no pattern was
 * emitted. A pattern WITHOUT recorded dimensions (pre-v2.5 snapshot) reads
 * "Legacy interpretation" — never back-derived.
 */
export function confidenceLabel(cell: TriangulationCell): string | null {
  if (cell.pattern === null || cell.pattern_qualifier === null) return null;
  if (cell.evidence_basis === null || cell.coverage_confidence === null) {
    return 'Legacy interpretation';
  }
  // Round 2: preliminary coverage OUTRANKS the declared-absent basis —
  // mirrors the server's deriveQualifier (suspected > provisional >
  // confirmed). "Confirmed by the two available legs" only at sufficient.
  const base =
    cell.evidence_basis === 'documented_missing'
      ? 'Suspected'
      : cell.coverage_confidence === 'preliminary'
        ? 'Provisional'
        : cell.evidence_basis === 'documented_declared_absent'
          ? 'Confirmed by the two available legs'
          : 'Confirmed';
  const details = cell.coverage_confidence === 'preliminary' ? preliminaryLegDetails(cell) : [];
  return details.length > 0 ? [base, ...details.map((d) => d.phrase)].join(' · ') : base;
}
